/**
 * AI 对话与表达优化服务
 * 负责：
 * 1. 与 DeepSeek 进行对话
 * 2. 分析用户输入并推荐知识库表达
 * 3. 知识库管理（添加、分类）
 */

// DeepSeek API 配置
const DEEPSEEK_CONFIG = {
  apiUrl: '/api/chat/completions',
  model: 'deepseek-chat',
  maxTokens: 1024,
  temperature: 0.7,
};

class ChatService {
  constructor() {
    this.conversationHistory = [];
  }

  // 检查 API 是否配置（后端代理方式，始终可用）
  isConfigured() {
    return true;
  }

  // 重置对话历史
  resetConversation() {
    this.conversationHistory = [];
  }

  /**
   * 发送消息到 DeepSeek
   * @param {string} systemPrompt - 系统提示词
   * @param {Array} messages - 消息历史
   * @returns {Promise<string>} AI 回复
   */
  async sendToDeepSeek(systemPrompt, messages) {
    if (!this.isConfigured()) {
      throw new Error('DeepSeek API 未配置');
    }

    try {
      const response = await fetch(DEEPSEEK_CONFIG.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: DEEPSEEK_CONFIG.model,
          messages: [
            { role: 'system', content: systemPrompt },
            ...messages,
          ],
          max_tokens: DEEPSEEK_CONFIG.maxTokens,
          temperature: DEEPSEEK_CONFIG.temperature,
        }),
      });

      if (!response.ok) {
        throw new Error(`API 请求失败: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0]?.message?.content || '';
    } catch (error) {
      console.error('DeepSeek API 调用失败:', error);
      throw error;
    }
  }

  /**
   * 分析用户输入，从知识库中推荐更好的表达
   * @param {string} userInput - 用户输入
   * @param {Array} knowledgeBase - 知识库（已过滤"老友"）
   * @returns {Promise<Array>} 推荐的表达列表 [{original, suggestion, meaning}]
   */
  async analyzeAndSuggest(userInput, knowledgeBase) {
    if (!userInput || knowledgeBase.length === 0) {
      return [];
    }

    // 构建知识库摘要（限制 token）
    const kbSummary = knowledgeBase.slice(0, 100).map(item => 
      `${item.content}: ${item.meaning}`
    ).join('\n');

    const systemPrompt = `你是一个英语表达优化助手。用户正在练习英语口语。
请分析用户的输入，找出可以用更好表达替换的部分。

知识库中的表达：
${kbSummary}

任务：
1. 分析用户输入中可以改进的词汇或短语
2. 从知识库中找出语义相关的更好表达（不只是同义词，要理解语境）
3. 最多返回 3 条建议
4. 如果没有合适的建议，返回空数组

返回 JSON 格式：
[
  {
    "original": "用户原文中的词/短语",
    "suggestion": "知识库中的表达",
    "meaning": "该表达的含义",
    "reason": "为什么这个表达更好（简短说明）"
  }
]

只返回 JSON，不要其他内容。如果没有建议，返回 []`;

    try {
      const result = await this.sendToDeepSeek(systemPrompt, [
        { role: 'user', content: userInput }
      ]);

      // 解析 JSON
      const jsonMatch = result.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      return [];
    } catch (error) {
      console.error('表达分析失败:', error);
      return [];
    }
  }

  /**
   * 生成对话回复
   * @param {string} userInput - 用户输入
   * @param {string} topic - 当前话题（可选）
   * @returns {Promise<string>} AI 回复
   */
  async generateResponse(userInput, topic = null) {
    const systemPrompt = `你是一个友好的英语口语练习伙伴。
${topic ? `当前话题：${topic}` : ''}

规则：
1. 用英语回复，保持对话自然流畅
2. 适当延伸话题，引导用户多说
3. 回复长度适中（2-4句话）
4. 可以提问来鼓励用户继续表达
5. 语气友好、鼓励性`;

    // 添加到对话历史
    this.conversationHistory.push({ role: 'user', content: userInput });

    try {
      const response = await this.sendToDeepSeek(systemPrompt, this.conversationHistory);
      
      // 添加 AI 回复到历史
      this.conversationHistory.push({ role: 'assistant', content: response });
      
      return response;
    } catch (error) {
      console.error('生成回复失败:', error);
      // 返回备用回复
      return "That's interesting! Could you tell me more about that?";
    }
  }

  /**
   * 综合分析用户表达：语法纠错 + 表达优化（KB优先，AI兜底）
   * @param {string} userText - 用户原文
   * @param {Array} candidates - 向量搜索返回的候选 [{content, meaning, matchedWord, similarity}]
   * @returns {Promise<Array>} [{type, original, correction, source?, meaning?, reason}]
   */
  async analyzeExpression(userText, candidates = []) {
    if (!userText) return [];
    if (!this.isConfigured()) {
      console.warn('DeepSeek 未配置，跳过分析');
      return [];
    }

    const candidateList = candidates.length > 0
      ? candidates.map(c =>
          `- "${c.matchedWord}" → "${c.content}" (meaning: ${c.meaning})`
        ).join('\n')
      : '(none)';

    const systemPrompt = `Review this English sentence. Return JSON array only.

"${userText}"

KB candidates: ${candidateList}

Output format — keep "original" as SHORT as possible (word or minimal phrase):
1. Grammar → {"type":"g","original":"wrong word(s)","correction":"fixed"}
2. Expression → {"type":"e","original":"word","correction":"better","source":"kb"|"ai","meaning":"中文(kb only)"}
   - KB first. If a word has 2-3 good KB alternatives, list ALL.
   - AI fallback only if KB has nothing AND word is clearly basic.

STRICT expression rules:
- The replacement must have the EXACT SAME meaning in THIS context. "afraid"(害怕) ≠ "awe"(敬畏) — REJECT.
- Match the sentence's tone. In casual speech like "I think...", do NOT suggest formal words like "contemplate".
- Only suggest if the replacement genuinely improves the sentence AND sounds natural in place.

No "reason" field needed. Return [] if nothing to fix.`;

    try {
      console.log('[analyzeExpression] 开始调用 DeepSeek，候选数:', candidates.length);
      const res = await fetch(DEEPSEEK_CONFIG.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getApiKey()}`,
        },
        body: JSON.stringify({
          model: DEEPSEEK_CONFIG.model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: 'Analyze.' },
          ],
          max_tokens: 2048,
          temperature: 0.5,
        }),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error('[analyzeExpression] API 错误:', res.status, errText);
        throw new Error(`API ${res.status}`);
      }
      const data = await res.json();
      const text = data.choices[0]?.message?.content || '';
      console.log('[analyzeExpression] DeepSeek 原始回复:', text.substring(0, 200) + '...');

      // 优先完整匹配
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[analyzeExpression] 解析结果:', parsed.length, '条');
        return parsed;
      }

      // 截断修复：找最后一个完整的 },\n 截断，补上 ]
      const bracketIdx = text.indexOf('[');
      if (bracketIdx >= 0) {
        let partial = text.substring(bracketIdx);
        const lastBrace = partial.lastIndexOf('}');
        if (lastBrace > 0) {
          partial = partial.substring(0, lastBrace + 1) + ']';
          try {
            const parsed = JSON.parse(partial);
            console.log('[analyzeExpression] 截断修复成功:', parsed.length, '条');
            return parsed;
          } catch (e) {
            console.warn('[analyzeExpression] 截断修复失败');
          }
        }
      }

      console.warn('[analyzeExpression] 未匹配到 JSON');
      return [];
    } catch (error) {
      console.error('[analyzeExpression] 失败:', error);
      return [];
    }
  }

  /**
   * 判断知识库条目的分类（单词/短语）
   * @param {string} content - 要分类的内容
   * @returns {Promise<string>} "单词" 或 "短语"
   */
  async classifyKnowledge(content) {
    const systemPrompt = `判断以下英语内容是"单词"还是"短语"。
- 单词：单个词汇
- 短语：多个词组成的表达、固定搭配、习语等

只回复"单词"或"短语"，不要其他内容。`;

    try {
      const result = await this.sendToDeepSeek(systemPrompt, [
        { role: 'user', content }
      ]);
      
      if (result.includes('短语')) return '短语';
      return '单词';
    } catch (error) {
      // 简单判断：包含空格则为短语
      return content.trim().includes(' ') ? '短语' : '单词';
    }
  }

  /**
   * 获取知识库条目的含义
   * @param {string} content - 英语表达
   * @returns {Promise<string>} 中文含义
   */
  async getMeaning(content) {
    const systemPrompt = `请给出以下英语表达的中文含义，简洁明了。
只返回含义，不要其他内容。`;

    try {
      const result = await this.sendToDeepSeek(systemPrompt, [
        { role: 'user', content }
      ]);
      return result.trim();
    } catch (error) {
      return '（含义待补充）';
    }
  }

  /**
   * 模拟回复（API 未配置时使用）
   */
  async simulateResponse(userInput) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const responses = [
      "That's a great point! Can you elaborate on that?",
      "Interesting perspective! What made you think of that?",
      "I see what you mean. How does that relate to your daily life?",
      "That's quite insightful! Have you always felt this way?",
      "Good observation! What other aspects would you like to explore?",
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }
}

// 单例
const chatService = new ChatService();
export default chatService;

